import React from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import { cn } from 'components/lib/utils';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';

interface FormFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  required?: boolean;
  type?: 'text' | 'url' | 'email';
  multiline?: boolean;
  rows?: number;
  fullWidth?: boolean;
  className?: string;
  dataTestId?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  placeholder,
  required,
  type = 'text',
  multiline,
  rows = 3,
  fullWidth,
  className,
  dataTestId
}) => {
  const id = name.replace(/\./g, '-');
  return (
    <div
      className={cn(
        'space-y-2',
        fullWidth && 'md:col-span-2',
        className
      )}
    >
      <Label
        htmlFor={id}
        className={cn(
          'inline-flex items-center text-sm font-medium text-base-foreground'
        )}
      >
        {required && (
          <div className="mr-2 inline-flex shrink-0 items-center" aria-hidden>
            <FontAwesomeIcon
              icon="fa-regular fa-asterisk"
              className="h-2 w-2 text-[8px] text-destructive"
            />
          </div>
        )}
        {label}
      </Label>
      {multiline ? (
        <Textarea
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          rows={rows}
          data-testid={dataTestId}
          aria-invalid={error || undefined}
          className={cn(
            'resize-none',
            'placeholder:text-base-mutedForeground',
            error &&
              'rounded-[7px] border border-base-destructive focus-visible:ring-base-destructive'
          )}
        />
      ) : (
        <Input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          data-testid={dataTestId}
          aria-invalid={error || undefined}
          className={cn(
            'placeholder:text-base-mutedForeground',
            error &&
              'rounded-[7px] border border-base-destructive focus-visible:ring-base-destructive'
          )}
        />
      )}
      {helperText && (
        <p
          className={cn(
            error
              ? 'font-sans text-sm font-normal leading-5 text-base-destructive'
              : 'text-xs text-base-mutedForeground'
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};
